import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import type { EChartsCoreOption } from 'echarts/core';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvLoadingItemComponent } from '../../../../Shared/Components/av-loading-item/av-loading-item.component';
import { M365LicenseReportFacade } from '../../../../../Facades/reports/m365-license.facade';
import { AvTableColumn, AvTableRow } from '../../../../Shared/Interfaces/av-table-card.interface';
import { exportAllToCsv } from '../../../../Shared/Utils/csv-export.utils';
import { ThemeService } from '../../../../Shared/Services/theme.service';
import {
  categoryAxis,
  CHART_ACCENT_GRAY,
  CHART_CATEGORY_ACCENTS,
  donutSeriesDefaults,
  integerValueAxis,
  legendBottom,
  readChartThemePalette,
} from '../../../utils/chart-theme.utils';
import { ReportDetailDialogData, ReportDetailSection } from '../../../models/report-dashboard.types';
import { ReportDetailDialogComponent } from '../../Dialogs/report-detail-dialog/report-detail-dialog.component';
import { ReportKpiTileComponent } from '../../Partials/report-kpi-tile/report-kpi-tile.component';
import { ReportKpiTileSeverity } from '../../../Types/report-kpi-tile-severity.type';
import { ReportChartCardComponent } from '../../Partials/report-chart-card/report-chart-card.component';

@Component({
  selector: 'app-m365-license-assignment-report',
  standalone: true,
  imports: [COMMON_IMPORTS, AvLoadingItemComponent, ReportKpiTileComponent, ReportChartCardComponent],
  templateUrl: './m365-license-assignment-report.component.html',
  styleUrl: './m365-license-assignment-report.component.scss',
})
export class M365LicenseAssignmentReportComponent {
  readonly facade = inject(M365LicenseReportFacade);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly theme = inject(ThemeService);
  private readonly translationsReady = toSignal(this.translate.onLangChange, { initialValue: null });

  private readonly licenseAssignmentColumns: AvTableColumn[] = [
    { key: 'displayName', label: 'app.reports.m365_license.columns.displayName', sortable: true },
    { key: 'userPrincipalName', label: 'app.reports.m365_license.columns.userPrincipalName', sortable: true },
    { key: 'accountEnabled', label: 'app.reports.m365_license.columns.accountEnabled', sortable: true, type: 'bool' },
    { key: 'lastSuccessfulSignIn', label: 'app.reports.m365_license.columns.lastSuccessfulSignIn', sortable: true },
    { key: 'licenseFriendlyName', label: 'app.reports.m365_license.columns.licenseFriendlyName', sortable: true },
    { key: 'assignmentType', label: 'app.reports.m365_license.columns.assignmentType', sortable: true },
    { key: 'inheritanceDetails', label: 'app.reports.m365_license.columns.inheritanceDetails', sortable: true },
  ];

  private readonly subscriptionOverviewColumns: AvTableColumn[] = [
    { key: 'subscriptionName', label: 'app.reports.m365_license.columns.subscriptionName', sortable: true },
    { key: 'createdDate', label: 'app.reports.m365_license.columns.createdDate', sortable: true },
    { key: 'endDate', label: 'app.reports.m365_license.columns.endDate', sortable: true },
    { key: 'licenseStatus', label: 'app.reports.m365_license.columns.licenseStatus', sortable: true },
    { key: 'consumedUnits', label: 'app.reports.m365_license.columns.consumedUnits', sortable: true },
    { key: 'totalLicenses', label: 'app.reports.m365_license.columns.totalLicenses', sortable: true },
    { key: 'availableLicenses', label: 'app.reports.m365_license.columns.availableLicenses', sortable: true },
  ];

  private readonly licenseAssignmentRows = computed<AvTableRow[]>(() =>
    this.facade.licenseAssignments().map(d => ({ ...d }))
  );

  private readonly subscriptionOverviewRows = computed<AvTableRow[]>(() =>
    this.facade.subscriptionOverview().map(d => ({ ...d }))
  );

  private readonly reclaimableRows = computed<AvTableRow[]>(() =>
    this.facade.reclaimableRows().map(d => ({ ...d }))
  );

  readonly sections: ReportDetailSection[] = [
    {
      key: 'licenseAssignments',
      titleKey: 'app.reports.m365_license.license_assignments',
      icon: 'badge',
      columns: this.licenseAssignmentColumns,
      rows: () => this.licenseAssignmentRows(),
    },
    {
      key: 'subscriptionOverview',
      titleKey: 'app.reports.m365_license.subscription_overview',
      icon: 'inventory_2',
      columns: this.subscriptionOverviewColumns,
      rows: () => this.subscriptionOverviewRows(),
    },
    {
      key: 'reclaimableLicenses',
      titleKey: 'app.reports.m365_license.reclaimable_licenses',
      icon: 'delete_sweep',
      columns: this.licenseAssignmentColumns,
      rows: () => this.reclaimableRows(),
    },
  ];

  readonly hasSubscriptionData = computed(() => this.facade.subscriptionOverview().length > 0);
  readonly totalSeatsValue = computed<number | null>(() =>
    this.hasSubscriptionData() ? this.facade.totalSeats() : null
  );
  readonly availableSeatsValue = computed<number | null>(() =>
    this.hasSubscriptionData() ? this.facade.availableSeats() : null
  );
  readonly utilizationDisplay = computed<string | null>(() =>
    this.formatUtilization(this.facade.utilization())
  );
  readonly wastedSeatsSeverity = computed<ReportKpiTileSeverity>(() =>
    this.facade.wastedSeats() > 0 ? 'warning' : 'neutral'
  );

  readonly assignmentTypeDonutOptions = signal<EChartsCoreOption | null>(null);
  readonly subscriptionUtilizationBarOptions = signal<EChartsCoreOption | null>(null);

  constructor() {
    this.registerAssignmentTypeDonutEffect();
    this.registerSubscriptionUtilizationBarEffect();
  }

  openSection(key: string): void {
    const section = this.sections.find(s => s.key === key);
    if (!section) return;
    this.openDialog(section.titleKey, section.icon, section.columns, section.rows());
  }

  openAssignmentTypeDialog(type: string): void {
    const rows = this.licenseAssignmentRows().filter(row => row['assignmentType'] === type);
    this.openDialog(this.assignmentTypeTitleKey(type), 'badge', this.licenseAssignmentColumns, rows);
  }

  onDonutSegmentClick(key: string): void {
    this.openAssignmentTypeDialog(key);
  }

  onSubscriptionBarSegmentClick(key: string): void {
    this.openSection(key);
  }

  exportAllCsv(): void {
    const exportSections = this.sections.filter(section => section.key !== 'reclaimableLicenses');
    exportAllToCsv(
      'm365-license-assignment-report',
      exportSections.map(section => ({
        title: this.translate.instant(section.titleKey),
        columns: section.columns,
        rows: section.rows(),
      })),
      key => this.translate.instant(key)
    );
  }

  private openDialog(titleKey: string, icon: string, columns: AvTableColumn[], rows: AvTableRow[]): void {
    this.dialog.open(ReportDetailDialogComponent, {
      width: '95vw',
      maxWidth: '95vw',
      height: '90vh',
      panelClass: 'report-detail-dialog-panel',
      data: { titleKey, icon, columns, rows } satisfies ReportDetailDialogData,
    });
  }

  private assignmentTypeTitleKey(type: string): string {
    if (type === 'Direct') return 'app.reports.m365_license.direct_licenses';
    if (type === 'Inherited') return 'app.reports.m365_license.inherited_licenses';
    return 'app.reports.m365_license.both_licenses';
  }

  private formatUtilization(utilization: number | null): string | null {
    return utilization === null ? null : `${Math.round(utilization * 100)}%`;
  }

  private registerAssignmentTypeDonutEffect(): void {
    effect(() => {
      this.theme.mode();
      this.translationsReady();
      this.assignmentTypeDonutOptions.set(this.buildAssignmentTypeDonutOptions());
    });
  }

  private registerSubscriptionUtilizationBarEffect(): void {
    effect(() => {
      this.theme.mode();
      this.translationsReady();
      this.subscriptionUtilizationBarOptions.set(this.buildSubscriptionUtilizationBarOptions());
    });
  }

  private buildAssignmentTypeDonutOptions(): EChartsCoreOption {
    const palette = readChartThemePalette();
    const data = this.facade.assignmentTypeDonutData().map((datum, index) => ({
      name: this.translate.instant(this.assignmentTypeTitleKey(datum.name)),
      value: datum.value,
      key: datum.key,
      itemStyle: { color: CHART_CATEGORY_ACCENTS[index % CHART_CATEGORY_ACCENTS.length] },
    }));

    const option = {
      tooltip: { trigger: 'item' as const, formatter: '{b}: {c} ({d}%)' },
      textStyle: { color: palette.text },
      legend: legendBottom(palette),
      series: [{ ...donutSeriesDefaults(), data }],
    };

    return option as EChartsCoreOption;
  }

  private buildSubscriptionUtilizationBarOptions(): EChartsCoreOption {
    const palette = readChartThemePalette();
    const bars = this.facade.subscriptionUtilizationBars();
    const consumedLabel = this.translate.instant('app.reports.m365_license.consumed_seats');
    const totalLabel = this.translate.instant('app.reports.m365_license.total_seats');

    const option = {
      tooltip: { trigger: 'axis' as const },
      legend: legendBottom(palette),
      grid: { left: 8, right: 16, bottom: 48, top: 12, containLabel: true },
      xAxis: categoryAxis(palette, bars.map(bar => bar.name)),
      yAxis: integerValueAxis(palette),
      series: [
        {
          name: consumedLabel,
          type: 'bar' as const,
          barMaxWidth: 28,
          itemStyle: { color: palette.primary, borderRadius: [4, 4, 0, 0] },
          data: bars.map(bar => ({ value: bar.consumed, key: 'subscriptionOverview' })),
        },
        {
          name: totalLabel,
          type: 'bar' as const,
          barMaxWidth: 28,
          itemStyle: { color: CHART_ACCENT_GRAY, borderRadius: [4, 4, 0, 0] },
          data: bars.map(bar => ({ value: bar.total, key: 'subscriptionOverview' })),
        },
      ],
    };

    return option as EChartsCoreOption;
  }
}
